// localStorage wrapper with TTL (Time-To-Live) management
import { getUserItem, setUserItem, removeUserItem, getCurrentUsername } from '../utils/userStorage';

const CACHE_VERSION = '1.0';

/**
 * Store data in localStorage with TTL
 * @param {string} key - Storage key
 * @param {*} data - Data to store
 * @param {number} ttl - Time to live in milliseconds
 */
export const setCache = (key, data, ttl) => {
    try {
        const cacheObject = {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + ttl,
            version: CACHE_VERSION
        };
        setUserItem(key, JSON.stringify(cacheObject));
    } catch (error) {
        // Handle quota exceeded error
        if (error.name === 'QuotaExceededError') {
            console.warn('⚠️ localStorage quota exceeded! Clearing old cache and retrying...');
            try {
                // Clear all expired caches first
                clearExpiredCaches();
                // Try again
                const cacheObject = {
                    data,
                    timestamp: Date.now(),
                    expiresAt: Date.now() + ttl,
                    version: CACHE_VERSION
                };
                setUserItem(key, JSON.stringify(cacheObject));
                console.log('✓ Cache saved successfully after cleanup');
            } catch (retryError) {
                console.warn('⚠️ Still can\'t cache - your anime list is too large for localStorage. App will work without cache.');
                // App continues to work, just without cache
            }
        } else {
            console.error('Error setting cache:', error);
        }
    }
};

/**
 * Get data from localStorage if not expired
 * @param {string} key - Storage key
 * @returns {*} Data if valid, null otherwise
 */
export const getCache = (key) => {
    try {
        const cached = getUserItem(key);
        if (!cached) return null;

        const cacheObject = JSON.parse(cached);

        // Check version
        if (cacheObject.version !== CACHE_VERSION) {
            clearCache(key);
            return null;
        }

        // Check if expired
        if (Date.now() > cacheObject.expiresAt) {
            clearCache(key);
            return null;
        }

        return cacheObject.data;
    } catch (error) {
        console.error('Error getting cache:', error);
        return null;
    }
};

/**
 * Check if cache exists and is valid
 * @param {string} key - Storage key
 * @returns {boolean} True if cache is valid
 */
export const isCacheValid = (key) => {
    try {
        const cached = getUserItem(key);
        if (!cached) return false;

        const cacheObject = JSON.parse(cached);
        return (
            cacheObject.version === CACHE_VERSION &&
            Date.now() <= cacheObject.expiresAt
        );
    } catch (error) {
        return false;
    }
};

/**
 * Get cache age in milliseconds
 * @param {string} key - Storage key
 * @returns {number} Age in milliseconds, -1 if not found
 */
export const getCacheAge = (key) => {
    try {
        const cached = getUserItem(key);
        if (!cached) return -1;

        const cacheObject = JSON.parse(cached);
        return Date.now() - cacheObject.timestamp;
    } catch (error) {
        return -1;
    }
};

/**
 * Clear specific cache entry
 * @param {string} key - Storage key
 */
export const clearCache = (key) => {
    try {
        removeUserItem(key);
    } catch (error) {
        console.error('Error clearing cache:', error);
    }
};

/**
 * Clear all app caches
 */
export const clearAllCache = () => {
    try {
        const username = getCurrentUsername();
        if (!username) return;

        const keys = Object.keys(localStorage);
        const prefix = `${username}_`;
        keys.forEach(key => {
            if (key.startsWith(prefix) && key.includes('anilist_')) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.error('Error clearing all cache:', error);
    }
};

/**
 * Clear only expired cache entries
 */
export const clearExpiredCaches = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('anilist_')) {
                try {
                    const cached = getUserItem(key);
                    if (cached) {
                        const cacheObject = JSON.parse(cached);
                        // Remove if expired or old version
                        if (Date.now() > cacheObject.expiresAt || cacheObject.version !== CACHE_VERSION) {
                            localStorage.removeItem(key);
                        }
                    }
                } catch (e) {
                    // If can't parse, remove it
                    localStorage.removeItem(key);
                }
            }
        });
    } catch (error) {
        console.error('Error clearing expired caches:', error);
    }
};

/**
 * Get simple value from localStorage (no TTL, no user prefix)
 * Used for identity keys (TOKEN, USERNAME) that must be accessible before login
 * @param {string} key - Storage key
 * @returns {string|null} Value or null
 */
export const getSimpleCache = (key) => {
    try {
        // Route token reads through tokenStorage for obfuscation support
        if (key === 'anilist_token' || key === 'accessToken') {
            const { getToken } = require('./tokenStorage');
            return getToken();
        }
        return localStorage.getItem(key);
    } catch (error) {
        console.error('Error getting simple cache:', error);
        return null;
    }
};

/**
 * Set simple value in localStorage (no TTL, no user prefix)
 * Used for identity keys (TOKEN, USERNAME) that must be accessible before login
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
export const setSimpleCache = (key, value) => {
    try {
        // Route token writes through tokenStorage for obfuscation
        if (key === 'anilist_token' || key === 'accessToken') {
            const { saveToken } = require('./tokenStorage');
            saveToken(value);
            return;
        }
        localStorage.setItem(key, value);
    } catch (error) {
        console.error('Error setting simple cache:', error);
    }
};
